import { IsoDateTime, IsoDateTimeTz, IsoDuration, IsoTime, IsoTimeTz } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';
import { addMillisecondsToDuration } from '../../helper/add-milliseconds-to-duration.function';
import { addMillisecondsToTime } from '../../helper/add-milliseconds-to-time.function';

/**
 * Add a number of milliseconds to an ISO date-time or time-only string (with or without timezone) or to an ISO 8601 duration.
 *
 * Supported inputs:
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz suffix
 * - ISO 8601 Duration: P... -> returns a duration string (may include fractional seconds)
 *
 * Notes:
 * - For date-only inputs (YYYY-MM-DD), time-based addition is not allowed and will throw.
 * - amount must be a finite integer in milliseconds; can be negative.
 */
export function addMilliseconds<T extends IsoDateTime | IsoDateTimeTz | IsoTime | IsoTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addMilliseconds: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'time'
    && deconstructed._type !== 'timeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addHours: invalid ISO datetime/time/duration string');
  }

  if(deconstructed._type === 'duration') {
    const newDuration = addMillisecondsToDuration(deconstructed, amount);
    return constructDate(newDuration) as unknown as T;
  }
  if(deconstructed._type === 'time' || deconstructed._type === 'timeTz') {
    const returnValue = addMillisecondsToTime(deconstructed, amount);
    return constructDate(returnValue) as unknown as T;
  }

  const {year, month, day, hours, minutes, seconds, milliseconds} = deconstructed;
  const dt = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds));
  dt.setMilliseconds(dt.getUTCMilliseconds() + amount);
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  deconstructed.hours = dt.getUTCHours();
  deconstructed.minutes = dt.getUTCMinutes();
  deconstructed.seconds = dt.getUTCSeconds();
  deconstructed.milliseconds = dt.getUTCMilliseconds();
  return constructDate(deconstructed) as unknown as T;
}
